<template>
  <div>
    <QuickstartCard
      v-if="$route.query.quickstart"
      data-intro="Let's get all of your agents loaded into Humaniz so they can take advantage of free training and setup pipeline reviews with you or your team."
    >
      <template #header>👋 Invite your agent roster</template>
      <template #body>
        Invite your agent roster to Humaniz. Once your agents are added, you can schedule pipeline reviews with them,
        assign them tasks, and give them access to training.
      </template>
    </QuickstartCard>
    <div v-if="!$store.state.general.loadingSpinner" class="flex flex-col">
      <DataTable :columns="dtColumns" :rows="rows" :click-fn="viewTeammate" :sort="sort" @sort-updated="handleNewSort">
        <template #filters="slotProps">
          <div class="flex justify-between items-center mb-4">
            <TeamListFilters
              :filters="filters"
              :eligible-reviewers="eligibleReviewers"
              @update-filter="handleNewFilter"
            />
            <div class="flex justify-end">
              <div>
                <TButtonSvg label="Message Team" color="white" @click="showMessageTeam = true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                    />
                  </svg>
                </TButtonSvg>
              </div>
              <div class="ml-2" data-intro="Use this button to import your agents using a CSV file.">
                <TButtonSvg label="Import" color="white" @click="showImportTeam = true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </TButtonSvg>
              </div>
              <div class="ml-2">
                <TButtonSvg label="Export" color="white" @click="exportContacts">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" />
                    <line x1="17" y1="7" x2="7" y2="17" />
                    <polyline points="8 7 17 7 17 16" />
                  </svg>
                </TButtonSvg>
              </div>
              <div class="ml-2" data-intro="Use this button to add an individual agent to your team.">
                <router-link to="/team/new">
                  <TButtonSvg label="New" color="indigo">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" />
                      <circle cx="12" cy="12" r="9" />
                      <line x1="9" y1="12" x2="15" y2="12" />
                      <line x1="12" y1="9" x2="12" y2="15" />
                    </svg>
                  </TButtonSvg>
                </router-link>
              </div>
            </div>
          </div>
        </template>
        <template #td-Name="slotProps">
          <div class="flex items-center">
            <div class="ml-4">
              <div class="text-sm leading-5 font-medium text-gray-900 truncate">
                {{ slotProps.row.Name }}
              </div>
              <div class="text-sm leading-5 text-gray-500 truncate">
                {{ slotProps.row.PhoneNumber }}
              </div>
            </div>
          </div>
        </template>
        <template #td-PipelineReviewCadence="slotProps">
          <div class="capitalize">
            {{ slotProps.row.PipelineReviewCadence }}
          </div>
        </template>
        <template #td-MostRecentPipelineReviewScore="slotProps">
          <div class="flex items-center gap-2">
            <PipelinePerformanceSmile :score="slotProps.row.MostRecentPipelineReviewScore || 0" />
          </div>
        </template>
      </DataTable>
    </div>

    <LoadingList />

    <Pagination :start-entry="startEntry" :end-entry="endEntry" :total="total" />

    <TeamMessage
      v-if="showMessageTeam"
      @handle-message-team="showMessageTeam = false"
      @handle-message-team-cancel="showMessageTeam = false"
    />
    <TeamImport
      v-if="showImportTeam"
      @handle-message-team="showImportTeam = false"
      @handle-message-team-cancel="showImportTeam = false"
    />
  </div>
</template>

<script>
import LoadingList from '../shared/LoadingList.vue';
import DataTable from '../shared/DataTable/index.vue';
import TeamListFilters from './TeamListFilters.vue';
import PipelinePerformanceSmile from '../PipelinePerformanceSmile/index.vue';
import TeamMessage from './TeamMessage.vue';
import { formatStandardDateTime } from '../../utilities/format.ts';
import Pagination from '../shared/Pagination.vue';
import RailsUtility from '../../utilities/rails.ts';
import QuickstartCard from '../shared/QuickstartCard.vue';
import TeamImport from './TeamImport.vue';

export default {
  name: 'TeamList',
  components: {
    LoadingList,
    DataTable,
    TeamListFilters,
    TeamMessage,
    Pagination,
    PipelinePerformanceSmile,
    QuickstartCard,
    TeamImport,
  },
  data() {
    return {
      showMessageTeam: false,
      showImportTeam: false,
      contacts: [],
      startEntry: 0,
      endEntry: 0,
      total: 0,
      totalActive: 0,
      totalInactive: 0,
      page: this.$route.query.page ? this.$route.query.page : 1,
      eligibleReviewers: [],
      loaded: false,
    };
  },
  computed: {
    dtColumns() {
      return [
        {
          label: 'Name',
          key: 'Name',
          sortable: true,
        },
        {
          label: 'Email',
          key: 'Email',
          sortable: true,
        },
        {
          label: 'Pipeline Reviewer',
          key: 'PipelineReviewer',
          sortable: true,
        },
        {
          label: 'Pipeline Review Cadence',
          key: 'PipelineReviewCadence',
          sortable: true,
        },
        {
          label: 'Most Recent Pipeline Review',
          key: 'MostRecentPipelineReviewDate',
          formatter: formatStandardDateTime,
          sortable: true,
        },
        {
          label: 'Most Recent Pipeline Review Score',
          key: 'MostRecentPipelineReviewScore',
          sortable: true,
        },
        {
          label: 'Created',
          key: 'Created',
          formatter: formatStandardDateTime,
          sortable: true,
        },
      ];
    },
    rows() {
      return this.contacts;
    },
    columns() {
      if (this.contacts.length > 0) {
        let columns = Object.keys(this.contacts[0]).filter(column => column != 'id');
        return columns;
      } else {
        return [];
      }
    },
    sort() {
      return {
        key: this.$route.query.sort,
        order: this.$route.query.order,
      };
    },
    filters() {
      const query = this.$route.query;

      let filters = [];

      filters.push({ key: 'status', value: query.status || 'All' });
      filters.push({ key: 'pipeline_reviewer', value: query.pipeline_reviewer || 'All' });

      return filters;
    },
  },
  mounted() {
    this.getContacts();
  },
  methods: {
    async viewTeammate(record) {
      const index = this.contacts.findIndex(c => c.id === record.id);
      this.$store.commit('general/setContactListContext', { currentIndex: index, records: this.contacts });
      await this.$router.push({ name: 'TeammateDetail', params: { id: record.id } });
    },
    async handleSelectedTab(e) {
      await this.$router.push({ path: '/team', query: { sort: 'created', order: 'desc', status: e.split(' (')[0] } });
      this.getContacts();
    },
    getContacts() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api${self.$route.fullPath}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          let fetchedContacts = [];
          for (let record of data.records) {
            let contact = {
              Name: record.name,
              PhoneNumber: record.phone_number,
              Email: record.email,
              Status: record.teammate_status,
              PipelineReviewer: record.assigned_pipeline_reviewer,
              PipelineReviewCadence: RailsUtility.convertFromEnumFormat(record.pipeline_review_cadence),
              MostRecentPipelineReviewScore: record.pipe_score || 0,
              MostRecentPipelineReviewDate: record.pipe_created_at,
              Created: record.created_at,
              id: record.id,
            };
            fetchedContacts.push(contact);
          }
          self.contacts = fetchedContacts;
          self.total = data.total;
          self.startEntry = data.start_entry;
          self.endEntry = data.end_entry;
          self.totalActive = data.total_active;
          self.totalInactive = data.total_inactive;
          self.eligibleReviewers = data.eligible_pipeline_reviewer_filters;
          self.loaded = true;
        });
    },
    exportContacts() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/team/download_csv`, {
        headers: {
          'Content-Type': 'text/csv',
        },
      })
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.blob();
        })
        .then(data => {
          let file = window.URL.createObjectURL(data);
          let a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          a.href = file;
          a.download = 'contact_export.csv';
          a.click();
          window.URL.revokeObjectURL(file);
        });
    },
    async handleNewSort(sort) {
      await this.$router.push({
        path: '/team',
        query: {
          sort: sort.key.toLowerCase(),
          order: sort.order,
          status: this.$route.query.status,
          pipeline_reviewer: this.$route.query.pipeline_reviewer,
        },
      });
      this.getContacts();
    },
    async handleNewFilter(newFilters, e) {
      const query = {
        sort: 'created',
        order: 'desc',
        status: newFilters.status.value || 'All',
        pipeline_reviewer: newFilters.pipeline_reviewer.value || 'All',
      };
      await this.$router.push({ path: '/team', query });
      this.getContacts();
    },
  },
};
</script>
